import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiErrorOf,
  type ApiClient,
  type ExportItem,
} from "../../shared/api/client";

export type ExportsApi = Pick<
  ApiClient,
  "listExports" | "requestExport" | "revokeExport" | "revokeAllExports"
>;

export type ExportNotice =
  | { kind: "requested" }
  | { kind: "in_progress" }
  | { kind: "cooldown"; retryAfter?: string }
  | { kind: "no_telegram" }
  | { kind: "failed" }
  | { kind: "links_failed" };

/** Active export links plus the actions that change them. */
export function useExports(api: ExportsApi) {
  const [items, setItems] = useState<readonly ExportItem[] | null>(null);
  const [notice, setNotice] = useState<ExportNotice | null>(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await api.listExports();
      if (mounted.current) setItems(next);
    } catch {
      if (mounted.current) {
        setItems((current) => current ?? []);
        setNotice({ kind: "links_failed" });
      }
    }
  }, [api]);

  useEffect(() => {
    let active = true;
    api
      .listExports()
      .then((next) => {
        if (active) setItems(next);
      })
      .catch(() => {
        if (!active) return;
        setItems((current) => current ?? []);
        setNotice({ kind: "links_failed" });
      });
    return () => {
      active = false;
    };
  }, [api]);

  const request = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await api.requestExport();
      if (mounted.current) setNotice({ kind: "requested" });
      await refresh();
    } catch (cause: unknown) {
      const error = apiErrorOf(cause);
      if (!mounted.current) return;
      if (error?.code === "export_cooldown")
        setNotice({ kind: "cooldown", retryAfter: error.retryAfter });
      else if (error?.code === "export_in_progress")
        setNotice({ kind: "in_progress" });
      else if (error?.code === "no_telegram_identity")
        setNotice({ kind: "no_telegram" });
      else setNotice({ kind: "failed" });
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const revoke = async (run: () => Promise<unknown>) => {
    setBusy(true);
    setNotice(null);
    try {
      await run();
      await refresh();
    } catch {
      if (mounted.current) setNotice({ kind: "links_failed" });
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return {
    items,
    notice,
    busy,
    request,
    revokeOne: (id: string) => revoke(() => api.revokeExport(id)),
    revokeAll: () => revoke(() => api.revokeAllExports()),
  };
}
